import axios from '@fs/zion-axios'
import { targetEnv } from '@fs/zion-config'
import { printDeprecationWarning } from '@fs/zion-debug'

let cachedContactPromises = {}

const missingIdErrorMessage = new Error(`Missing id in fetchContact. Either a cisId or cpUserId is expected`)

/**
 * Handles contact promise resolution and caching
 * @param {Object} params - The parameters object
 * @param {Promise} params.contactPromise - Promise that resolves to contact data
 * @param {string} [params.cisId] - The CIS ID for caching
 * @param {string} [params.cpUserId] - The CP User ID for caching
 * @returns {Promise<Object>} The contact data
 * @throws {Error} Re-throws any error from the contact promise
 */
async function returnContact({ contactPromise, cisId, cpUserId }) {
  cachedContactPromises[cisId || cpUserId] = contactPromise
  try {
    const contact = (await contactPromise).data
    contactPromise[contact.cpUserId] = contact
    contactPromise[contact.id] = contact
    return contact
  } catch (error) {
    cachedContactPromises[cisId || cpUserId] = null
    throw error
  }
}

/**
 * Maps contact data from CIS Admin format to FT User format
 * @param {Object} cisAdminContact - Contact data from CIS Admin service
 * @returns {Object} Contact data in FT User format
 */
function mapContactToFtUserShape(cisAdminContact) {
  return {
    id: cisAdminContact.id,
    contactName: cisAdminContact.contactName,
    email: cisAdminContact.email,
    alternateEmail: null,
    phoneNumber: cisAdminContact?.sms?.number,
    mailingAddress: null,
    preferredLanguage: cisAdminContact.preferredLanguage,
    aliases: [],
    givenName: cisAdminContact.givenName,
    familyName: cisAdminContact.familyName,
    country: cisAdminContact.country,
    displayName: cisAdminContact.displayName,
  }
}

/**
 * Fetches contact data from CIS Admin service
 * @param {string} cisId - The CIS ID to fetch contact for
 * @returns {Promise<{data: Object}>} Contact data wrapped in data property
 * @throws {Error} Throws error if API request fails
 */
async function getContactCisAdminService(cisId) {
  const url = `/service/ident/cis/cis-admin-api/v1/user/${cisId}`
  const response = await axios.get(url, {
    headers: { Accept: 'application/familysearch-identity-result+v4.2+json' },
    fsUserAgentChainSuffix: 'zion-user',
  })

  const contact = response.data.users[0]
  // wrapping the contact behind `data` so the API of returnContact won't need to change
  return { data: mapContactToFtUserShape(contact) }
}

/**
 * Fetches contact data by CIS ID
 * @param {string} cisId - The CIS ID to fetch contact for
 * @returns {Promise<Object>} The contact data
 * @throws {Error} Throws error if cisId is missing or API request fails
 */
export async function fetchContactByCisId(cisId) {
  if (!cisId) throw missingIdErrorMessage
  return returnContact({
    contactPromise: cachedContactPromises[cisId] || getContactCisAdminService(cisId),
    cisId,
  })
}

/**
 * @deprecated
 *
 * Fetches contact data by CP User ID
 * @param {string} cpUserId - The CP User ID to fetch contact for
 * @returns {Promise<Object>} The contact data with cpUserId property added
 * @throws {Error} Throws error if cpUserId is missing or API request fails
 */
export async function fetchContactByCpUserId(cpUserId) {
  if (targetEnv !== 'prod') {
    printDeprecationWarning(`Deprecation warning: Fetching a contact by cpUserId is deprecated.
   Most usages of this function (fetchContactByCpUserId) are just to map a cpUserId to a cisId.
   If that is what you want, use the async function getCisIdFromCpUserId from zion-user`)
  }

  if (!cpUserId) throw missingIdErrorMessage

  // we first have to map from cpUserId to cisId, and then make the call to cis-admin with the cisId
  const cisId = await getCisIdFromCpUserId(cpUserId)
  return {
    ...(await returnContact({
      contactPromise: cachedContactPromises[cisId] || getContactCisAdminService(cisId),
      cpUserId,
    })),
    cpUserId,
  }
}

/**
 * Maps cpUserId to cisId by querying the tree-data tree service
 * @param {string} cpUserId - The cpUserId to map
 * @returns {Promise<string>} The corresponding cisId
 * @throws {Error} Throws error if API request fails
 */
export async function getCisIdFromCpUserId(cpUserId) {
  const res = await axios.get(`/service/tree/tree-data/user/${cpUserId}/contact-info`, {
    fsUserAgentChainSuffix: 'zion-user',
  })
  return res?.data?.data?.cisUserId
}

/**
 * Generic function to fetch contact data by either CIS ID or CP User ID
 * @param {Object} params - The parameters object
 * @param {string} [params.cisId] - The CIS ID to fetch contact for
 * @param {string} [params.cpUserId] - The CP User ID to fetch contact for
 * @returns {Promise<Object>} The contact data
 * @throws {Error} Throws error if neither cisId nor cpUserId is provided, or if API request fails
 */
export async function fetchContact({ cisId, cpUserId }) {
  if (cisId) {
    return fetchContactByCisId(cisId)
  }
  if (cpUserId) {
    return fetchContactByCpUserId(cpUserId)
  }
  throw missingIdErrorMessage
}

/**
 * Clears the cached contact promises - used for testing purposes
 * @returns {void}
 */
export function clearCacheForTest() {
  cachedContactPromises = {}
}
